// frontend/src/pages/superAdmin/ResetScores.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import Footer from '../../components/Footer';
import api from '../../services/api';
import Swal from 'sweetalert2';

const ResetScores = ({ user, setUser }) => {
  const [schools, setSchools] = useState([]);
  const [fiscalYears, setFiscalYears] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState('');
  const [selectedFiscalYear, setSelectedFiscalYear] = useState('');
  const [loading, setLoading] = useState(false);
  
  // เปลี่ยนจาก respondentCounts เป็น scores
  const [scores, setScores] = useState({
    iit: 0,
    eit1: 0,
    eit2: 0,
    oit: 0,
    total: 0
  });
  
  // เพิ่ม state ใหม่สำหรับเก็บข้อมูลภาพรวมทุกโรงเรียน
  const [allSchoolsData, setAllSchoolsData] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  
  // เพิ่ม state สำหรับเก็บข้อมูลจำนวนผู้ตอบแบบสอบถาม (ยังคงต้องใช้เพื่อการตรวจสอบปุ่มรีเซ็ต)
  const [respondentCounts, setRespondentCounts] = useState({
    iit: 0,
    eit1: 0,
    eit2: 0,
    oit: 0
  });

  // โหลดข้อมูลโรงเรียนและปีงบประมาณ
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [schoolsRes, fiscalYearsRes] = await Promise.all([
          api.get('/schools'),
          api.get('/fiscal-years')
        ]);
        
        setSchools(schoolsRes.data);
        setFiscalYears(fiscalYearsRes.data);
        
        // ดึงปีงบประมาณปัจจุบันและตั้งค่าเริ่มต้น
        const activeYear = fiscalYearsRes.data.find(year => year.active);
        if (activeYear) {
          setSelectedFiscalYear(activeYear.id);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        Swal.fire({
          icon: 'error',
          title: 'เกิดข้อผิดพลาด',
          text: 'ไม่สามารถโหลดข้อมูลได้'
        });
      }
    };
    
    fetchData();
  }, []);

  // โหลดข้อมูลทั้งหมดเมื่อเลือกปีงบประมาณ
  useEffect(() => {
    if (!selectedFiscalYear) return;
    
    fetchAllSchoolsData();
  }, [selectedFiscalYear]);

  // ฟังก์ชันใหม่สำหรับดึงข้อมูลภาพรวมทุกโรงเรียน
  const fetchAllSchoolsData = async () => {
    try {
      setTableLoading(true);
      
      // ดึงข้อมูลโควต้าของทุกโรงเรียน (ข้อมูลนี้มีจำนวนผู้ตอบแบบสอบถามด้วย)
      const quotasRes = await api.get(`/quotas/${selectedFiscalYear}`);
      
      // สร้างข้อมูลเริ่มต้นสำหรับทุกโรงเรียน
      const initialData = [];
      
      // ดึงข้อมูลคะแนนแต่ละโรงเรียน
      for (const school of schools) {
        try {
          // ดึงข้อมูลจำนวนผู้ตอบแบบสอบถาม
          const countRes = await api.get(`/respondents/count/${school.id}/${selectedFiscalYear}`);
          const oitRes = await api.get(`/oit/${school.id}/${selectedFiscalYear}`);
          
          const iitCount = countRes.data.iit.current;
          const eit1Count = countRes.data.eit1.current;
          const eit2Count = countRes.data.eit2.current;
          const oitCount = oitRes.data.length;
          
          // ดึงข้อมูลคะแนน
          let iitScore = 0;
          let eit1Score = 0;
          let eit2Score = 0;
          let eitScore = 0;
          let oitScore = 0;
          let totalScore = 0;
          
          // ดึงคะแนนเฉพาะโรงเรียนที่มีข้อมูลตอบแบบสอบถามแล้ว
          if (iitCount > 0 || eit1Count > 0 || eit2Count > 0 || oitCount > 0) {
            try {
              const totalRes = await api.get(`/reports/total/${school.id}/${selectedFiscalYear}`);
              iitScore = parseFloat(totalRes.data.iit_score).toFixed(2);
              eit1Score = parseFloat(totalRes.data.eit1_score).toFixed(2);
              eit2Score = parseFloat(totalRes.data.eit2_score).toFixed(2);
              eitScore = parseFloat(totalRes.data.eit_score).toFixed(2);
              oitScore = parseFloat(totalRes.data.oit_score).toFixed(2);
              totalScore = parseFloat(totalRes.data.total_score).toFixed(2);
            } catch (error) {
              console.error(`Error fetching scores for school ${school.name}:`, error);
            }
          }
          
          initialData.push({
            id: school.id,
            code: school.code,
            name: school.name,
            iit_count: iitCount,
            eit1_count: eit1Count,
            eit2_count: eit2Count,
            oit_count: oitCount,
            iit_score: iitScore,
            eit1_score: eit1Score,
            eit2_score: eit2Score,
            eit_score: eitScore,
            oit_score: oitScore,
            total_score: totalScore
          });
        } catch (error) {
          console.error(`Error fetching data for school ${school.name}:`, error);
          initialData.push({
            id: school.id,
            code: school.code,
            name: school.name,
            iit_count: 0,
            eit1_count: 0,
            eit2_count: 0,
            oit_count: 0,
            iit_score: 0,
            eit1_score: 0,
            eit2_score: 0,
            eit_score: 0,
            oit_score: 0,
            total_score: 0
          });
        }
      }
      
      // เรียงลำดับตามคะแนนรวมจากมากไปน้อย
      initialData.sort((a, b) => {
        return parseFloat(b.total_score) - parseFloat(a.total_score);
      });
      
      setAllSchoolsData(initialData);
    } catch (error) {
      console.error('Error fetching all schools data:', error);
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถโหลดข้อมูลภาพรวมได้'
      });
    } finally {
      setTableLoading(false);
    }
  };

  // โหลดข้อมูลจำนวนผู้ตอบแบบสอบถามและคะแนนเมื่อเลือกโรงเรียนและปีงบประมาณ
  useEffect(() => {
    const fetchData = async () => {
      if (!selectedSchool || !selectedFiscalYear) return;
      
      try {
        setLoading(true);
        
        // ดึงข้อมูลจำนวนผู้ตอบแบบสอบถาม IIT, EIT1, EIT2
        const countRes = await api.get(`/respondents/count/${selectedSchool}/${selectedFiscalYear}`);
        
        // ดึงข้อมูลการส่ง OIT
        const oitRes = await api.get(`/oit/${selectedSchool}/${selectedFiscalYear}`);
        
        setRespondentCounts({
          iit: countRes.data.iit.current,
          eit1: countRes.data.eit1.current,
          eit2: countRes.data.eit2.current,
          oit: oitRes.data.length
        });
        
        // ดึงข้อมูลคะแนนเฉพาะเมื่อมีการตอบแบบสอบถามแล้ว
        if (countRes.data.iit.current > 0 || countRes.data.eit1.current > 0 || 
            countRes.data.eit2.current > 0 || oitRes.data.length > 0) {
          try {
            const totalRes = await api.get(`/reports/total/${selectedSchool}/${selectedFiscalYear}`);
            
            setScores({
              iit: parseFloat(totalRes.data.iit_score).toFixed(2),
              eit1: parseFloat(totalRes.data.eit1_score).toFixed(2),
              eit2: parseFloat(totalRes.data.eit2_score).toFixed(2),
              oit: parseFloat(totalRes.data.oit_score).toFixed(2),
              total: parseFloat(totalRes.data.total_score).toFixed(2)
            });
          } catch (error) {
            console.error('Error fetching scores:', error);
            setScores({
              iit: 0,
              eit1: 0,
              eit2: 0,
              oit: 0,
              total: 0
            });
          }
        } else {
          setScores({
            iit: 0,
            eit1: 0,
            eit2: 0,
            oit: 0,
            total: 0
          });
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [selectedSchool, selectedFiscalYear]);

  // ฟังก์ชันสำหรับรีเซ็ตคะแนน IIT
  const handleResetIIT = async (schoolId = null) => {
    const school_id = schoolId || selectedSchool;
    
    if (!school_id || !selectedFiscalYear) {
      Swal.fire({
        icon: 'warning',
        title: 'กรุณาเลือกข้อมูลให้ครบถ้วน',
        text: 'โปรดเลือกโรงเรียนและปีงบประมาณ'
      });
      return;
    }

    const schoolName = schools.find(s => s.id === parseInt(school_id))?.name;
    const fiscalYear = fiscalYears.find(y => y.id === parseInt(selectedFiscalYear))?.year;

    // ถามยืนยันก่อนรีเซ็ต
    const result = await Swal.fire({
      icon: 'warning',
      title: 'ยืนยันการรีเซ็ตคะแนน IIT',
      text: `คุณต้องการรีเซ็ตคะแนน IIT ของโรงเรียน ${schoolName} ปีงบประมาณ ${fiscalYear} ใช่หรือไม่?`,
      showCancelButton: true,
      confirmButtonText: 'ยืนยัน',
      cancelButtonText: 'ยกเลิก',
      confirmButtonColor: '#d33'
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);
        const response = await api.post('/reset/iit', {
          school_id: school_id,
          fiscal_year_id: selectedFiscalYear
        });
        
        Swal.fire({
          icon: 'success',
          title: 'สำเร็จ',
          text: response.data.message
        });
        
        // อัปเดตข้อมูลจำนวนผู้ตอบแบบสอบถามและคะแนน
        if (school_id === selectedSchool) {
          setRespondentCounts(prev => ({
            ...prev,
            iit: 0
          }));
          
          setScores(prev => ({
            ...prev,
            iit: 0,
            total: (parseFloat(prev.eit1) + parseFloat(prev.eit2) + parseFloat(prev.oit)).toFixed(2)
          }));
        }
        
        // รีเฟรชข้อมูลตาราง
        fetchAllSchoolsData();
      } catch (error) {
        console.error('Error resetting IIT scores:', error);
        Swal.fire({
          icon: 'error',
          title: 'เกิดข้อผิดพลาด',
          text: 'ไม่สามารถรีเซ็ตคะแนน IIT ได้'
        });
      } finally {
        setLoading(false);
      }
    }
  };

  // ฟังก์ชันสำหรับรีเซ็ตคะแนน EIT1
  const handleResetEIT1 = async (schoolId = null) => {
    const school_id = schoolId || selectedSchool;
    
    if (!school_id || !selectedFiscalYear) {
      Swal.fire({
        icon: 'warning',
        title: 'กรุณาเลือกข้อมูลให้ครบถ้วน',
        text: 'โปรดเลือกโรงเรียนและปีงบประมาณ'
      });
      return;
    }

    const schoolName = schools.find(s => s.id === parseInt(school_id))?.name;
    const fiscalYear = fiscalYears.find(y => y.id === parseInt(selectedFiscalYear))?.year;

    const result = await Swal.fire({
      icon: 'warning',
      title: 'ยืนยันการรีเซ็ตคะแนน EIT1',
      text: `คุณต้องการรีเซ็ตคะแนน EIT1 ของโรงเรียน ${schoolName} ปีงบประมาณ ${fiscalYear} ใช่หรือไม่?`,
      showCancelButton: true,
      confirmButtonText: 'ยืนยัน',
      cancelButtonText: 'ยกเลิก',
      confirmButtonColor: '#d33'
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);
        const response = await api.post('/reset/eit1', {
          school_id: school_id,
          fiscal_year_id: selectedFiscalYear
        });
        
        Swal.fire({
          icon: 'success',
          title: 'สำเร็จ',
          text: response.data.message
        });
        
        if (school_id === selectedSchool) {
          setRespondentCounts(prev => ({
            ...prev,
            eit1: 0
          }));
          
          setScores(prev => ({
            ...prev,
            eit1: 0,
            total: (parseFloat(prev.iit) + parseFloat(prev.eit2) + parseFloat(prev.oit)).toFixed(2)
          }));
        }
        
        // รีเฟรชข้อมูลตาราง
        fetchAllSchoolsData();
      } catch (error) {
        console.error('Error resetting EIT1 scores:', error);
        Swal.fire({
          icon: 'error',
          title: 'เกิดข้อผิดพลาด',
          text: 'ไม่สามารถรีเซ็ตคะแนน EIT1 ได้'
        });
      } finally {
        setLoading(false);
      }
    }
  };

  // ฟังก์ชันสำหรับรีเซ็ตคะแนน EIT2
  const handleResetEIT2 = async (schoolId = null) => {
    const school_id = schoolId || selectedSchool;
    
    if (!school_id || !selectedFiscalYear) {
      Swal.fire({
        icon: 'warning',
        title: 'กรุณาเลือกข้อมูลให้ครบถ้วน',
        text: 'โปรดเลือกโรงเรียนและปีงบประมาณ'
      });
      return;
    }

    const schoolName = schools.find(s => s.id === parseInt(school_id))?.name;
    const fiscalYear = fiscalYears.find(y => y.id === parseInt(selectedFiscalYear))?.year;

    const result = await Swal.fire({
      icon: 'warning',
      title: 'ยืนยันการรีเซ็ตคะแนน EIT2',
      text: `คุณต้องการรีเซ็ตคะแนน EIT2 ของโรงเรียน ${schoolName} ปีงบประมาณ ${fiscalYear} ใช่หรือไม่?`,
      showCancelButton: true,
      confirmButtonText: 'ยืนยัน',
      cancelButtonText: 'ยกเลิก',
      confirmButtonColor: '#d33'
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);
        const response = await api.post('/reset/eit2', {
          school_id: school_id,
          fiscal_year_id: selectedFiscalYear
        });
        
        Swal.fire({
          icon: 'success',
          title: 'สำเร็จ',
          text: response.data.message
        });
        
        if (school_id === selectedSchool) {
          setRespondentCounts(prev => ({
            ...prev,
            eit2: 0
          }));
          
          setScores(prev => ({
            ...prev,
            eit2: 0,
            total: (parseFloat(prev.iit) + parseFloat(prev.eit1) + parseFloat(prev.oit)).toFixed(2)
          }));
        }
        
        // รีเฟรชข้อมูลตาราง
        fetchAllSchoolsData();
      } catch (error) {
        console.error('Error resetting EIT2 scores:', error);
        Swal.fire({
          icon: 'error',
          title: 'เกิดข้อผิดพลาด',
          text: 'ไม่สามารถรีเซ็ตคะแนน EIT2 ได้'
        });
      } finally {
        setLoading(false);
      }
    }
  };

  // ฟังก์ชันสำหรับรีเซ็ตคะแนน OIT
  const handleResetOIT = async (schoolId = null) => {
    const school_id = schoolId || selectedSchool;
    
    if (!school_id || !selectedFiscalYear) {
      Swal.fire({
        icon: 'warning',
        title: 'กรุณาเลือกข้อมูลให้ครบถ้วน',
        text: 'โปรดเลือกโรงเรียนและปีงบประมาณ'
      });
      return;
    }

    const schoolName = schools.find(s => s.id === parseInt(school_id))?.name;
    const fiscalYear = fiscalYears.find(y => y.id === parseInt(selectedFiscalYear))?.year;

    const result = await Swal.fire({
      icon: 'warning',
      title: 'ยืนยันการรีเซ็ตคะแนน OIT',
      text: `คุณต้องการรีเซ็ตคะแนน OIT ของโรงเรียน ${schoolName} ปีงบประมาณ ${fiscalYear} ใช่หรือไม่?`,
      showCancelButton: true,
      confirmButtonText: 'ยืนยัน',
      cancelButtonText: 'ยกเลิก',
      confirmButtonColor: '#d33'
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);
        const response = await api.post('/reset/oit', {
          school_id: school_id,
          fiscal_year_id: selectedFiscalYear
        });
        
        Swal.fire({
          icon: 'success',
          title: 'สำเร็จ',
          text: response.data.message
        });
        
        if (school_id === selectedSchool) {
          setRespondentCounts(prev => ({
            ...prev,
            oit: 0
          }));
          
          setScores(prev => ({
            ...prev,
            oit: 0,
            total: (parseFloat(prev.iit) + parseFloat(prev.eit1) + parseFloat(prev.eit2)).toFixed(2)
          }));
        }
        
        // รีเฟรชข้อมูลตาราง
        fetchAllSchoolsData();
      } catch (error) {
        console.error('Error resetting OIT scores:', error);
        Swal.fire({
          icon: 'error',
          title: 'เกิดข้อผิดพลาด',
          text: 'ไม่สามารถรีเซ็ตคะแนน OIT ได้'
        });
      } finally {
        setLoading(false);
      }
    }
  };

  // ฟังก์ชันสำหรับรีเซ็ตคะแนนทั้งหมด
  const handleResetAll = async (schoolId = null) => {
    const school_id = schoolId || selectedSchool;
    
    if (!school_id || !selectedFiscalYear) {
      Swal.fire({
        icon: 'warning',
        title: 'กรุณาเลือกข้อมูลให้ครบถ้วน',
        text: 'โปรดเลือกโรงเรียนและปีงบประมาณ'
      });
      return;
    }

    const schoolName = schools.find(s => s.id === parseInt(school_id))?.name;
    const fiscalYear = fiscalYears.find(y => y.id === parseInt(selectedFiscalYear))?.year;

    const result = await Swal.fire({
      icon: 'warning',
      title: 'ยืนยันการรีเซ็ตคะแนนทั้งหมด',
      text: `คุณต้องการรีเซ็ตคะแนนทั้งหมดของโรงเรียน ${schoolName} ปีงบประมาณ ${fiscalYear} ใช่หรือไม่?`,
      showCancelButton: true,
      confirmButtonText: 'ยืนยัน',
      cancelButtonText: 'ยกเลิก',
      confirmButtonColor: '#d33'
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);
        const response = await api.post('/reset/all', {
          school_id: school_id,
          fiscal_year_id: selectedFiscalYear
        });
        
        Swal.fire({
          icon: 'success',
          title: 'สำเร็จ',
          text: response.data.message
        });
        
        // รีเซ็ตค่าทั้งหมด
        if (school_id === selectedSchool) {
          setRespondentCounts({
            iit: 0,
            eit1: 0,
            eit2: 0,
            oit: 0
          });
          
          setScores({
            iit: 0,
            eit1: 0,
            eit2: 0,
            oit: 0,
            total: 0
          });
        }
        
        // รีเฟรชข้อมูลตาราง
        fetchAllSchoolsData();
      } catch (error) {
        console.error('Error resetting all scores:', error);
        Swal.fire({
          icon: 'error',
          title: 'เกิดข้อผิดพลาด',
          text: 'ไม่สามารถรีเซ็ตคะแนนทั้งหมดได้'
        });
      } finally {
        setLoading(false);
      }
    }
  };

  // ฟังก์ชันสำหรับแสดงเกณฑ์การประเมิน (rating) ตามคะแนน
  const getRating = (score) => {
    if (score >= 95) return 'ผ่านดีเยี่ยม (AA)';
    if (score >= 85) return 'ผ่านดี (A)';
    if (score >= 75) return 'ผ่าน (B)';
    if (score >= 65) return 'ต้องปรับปรุง (C)';
    if (score > 0) return 'ต้องปรับปรุงโดยด่วน (D)';
    return '-';
  };

  // ฟังก์ชันสำหรับแสดงสีตามคะแนน
  const getScoreClass = (score) => {
    if (score >= 95) return 'text-success font-weight-bold';
    if (score >= 85) return 'text-success';
    if (score >= 75) return 'text-primary';
    if (score >= 65) return 'text-warning';
    if (score > 0) return 'text-danger';
    return '';
  };

  return (
    <div className="wrapper">
      <Header user={user} title="รีเซ็ตคะแนนการตอบแบบสอบถาม" />
      <Sidebar user={user} />

      <div className="content-wrapper">
        <section className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h1>รีเซ็ตคะแนนการตอบแบบสอบถาม</h1>
              </div>
              <div className="col-sm-6">
                <ol className="breadcrumb float-sm-right">
                  <li className="breadcrumb-item">
                    <Link to="/super-admin/dashboard">หน้าหลัก</Link>
                  </li>
                  <li className="breadcrumb-item active">รีเซ็ตคะแนน</li>
                </ol>
              </div>
            </div>
          </div>
        </section>

        <section className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="col-12">
                <div className="card">
                  <div className="card-header">
                    <h3 className="card-title">เลือกโรงเรียนและปีงบประมาณ</h3>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>โรงเรียน</label>
                          <select
                            className="form-control"
                            value={selectedSchool}
                            onChange={(e) => setSelectedSchool(e.target.value)}
                            disabled={loading}
                          >
                            <option value="">เลือกโรงเรียน</option>
                            {schools.map((school) => (
                              <option key={school.id} value={school.id}>
                                {school.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>ปีงบประมาณ</label>
                          <select
                            className="form-control"
                            value={selectedFiscalYear}
                            onChange={(e) => setSelectedFiscalYear(e.target.value)}
                            disabled={loading}
                          >
                            <option value="">เลือกปีงบประมาณ</option>
                            {fiscalYears.map((year) => (
                              <option key={year.id} value={year.id}>
                                {year.year} {year.active ? '(ปัจจุบัน)' : ''}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {selectedSchool && selectedFiscalYear && (
              <div className="row">
                <div className="col-12">
                  <div className="card card-primary">
                    <div className="card-header">
                      <h3 className="card-title">สรุปคะแนนและการรีเซ็ต</h3>
                    </div>
                    <div className="card-body">
                      <div className="alert alert-warning">
                        <h5><i className="icon fas fa-exclamation-triangle"></i> คำเตือน!</h5>
                        การรีเซ็ตคะแนนจะลบข้อมูลการตอบแบบสอบถามทั้งหมด และไม่สามารถกู้คืนได้
                      </div>

                      {/* แสดงคะแนนของโรงเรียนที่เลือก */}
                      <div className="row mb-4">
                        <div className="col-12">
                          <div className="card bg-light">
                            <div className="card-body">
                              <h5 className="card-title">
                                {schools.find(s => s.id === parseInt(selectedSchool))?.name} - 
                                ปีงบประมาณ {fiscalYears.find(y => y.id === parseInt(selectedFiscalYear))?.year}
                              </h5>
                              <div className="row">
                                <div className="col-md-2">
                                  <div className="small-box bg-info">
                                    <div className="inner text-center">
                                      <h3>{scores.iit}</h3>
                                      <p>คะแนน IIT<br/>(30 คะแนน)</p>
                                    </div>
                                    <div className="icon">
                                      <i className="fas fa-users"></i>
                                    </div>
                                  </div>
                                </div>
                                <div className="col-md-2">
                                  <div className="small-box bg-success">
                                    <div className="inner text-center">
                                      <h3>{scores.eit1}</h3>
                                      <p>คะแนน EIT1<br/>(15 คะแนน)</p>
                                    </div>
                                    <div className="icon">
                                      <i className="fas fa-user-friends"></i>
                                    </div>
                                  </div>
                                </div>
                                <div className="col-md-2">
                                  <div className="small-box bg-warning">
                                    <div className="inner text-center">
                                      <h3>{scores.eit2}</h3>
                                      <p>คะแนน EIT2<br/>(15 คะแนน)</p>
                                    </div>
                                    <div className="icon">
                                      <i className="fas fa-user-friends"></i>
                                    </div>
                                  </div>
                                </div>
                                <div className="col-md-2">
                                  <div className="small-box bg-primary">
                                    <div className="inner text-center">
                                      <h3>{scores.oit}</h3>
                                      <p>คะแนน OIT<br/>(40 คะแนน)</p>
                                    </div>
                                    <div className="icon">
                                      <i className="fas fa-link"></i>
                                    </div>
                                  </div>
                                </div>
                                <div className="col-md-4">
                                  <div className="small-box bg-dark">
                                    <div className="inner text-center">
                                      <h3>{scores.total}</h3>
                                      <p>คะแนนรวม<br/>{getRating(scores.total)}</p>
                                    </div>
                                    <div className="icon">
                                      <i className="fas fa-chart-line"></i>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-3">
                          <button
                            className="btn btn-danger btn-block"
                            onClick={() => handleResetIIT()}
                            disabled={loading || respondentCounts.iit === 0}
                          >
                            <i className="fas fa-trash-alt"></i> รีเซ็ตคะแนน IIT
                          </button>
                        </div>

                        <div className="col-md-3">
                          <button
                            className="btn btn-danger btn-block"
                            onClick={() => handleResetEIT1()}
                            disabled={loading || respondentCounts.eit1 === 0}
                          >
                            <i className="fas fa-trash-alt"></i> รีเซ็ตคะแนน EIT1
                          </button>
                        </div>

                        <div className="col-md-3">
                          <button
                            className="btn btn-danger btn-block"
                            onClick={() => handleResetEIT2()}
                            disabled={loading || respondentCounts.eit2 === 0}
                          >
                            <i className="fas fa-trash-alt"></i> รีเซ็ตคะแนน EIT2
                          </button>
                        </div>

                        <div className="col-md-3">
                          <button
                            className="btn btn-danger btn-block"
                            onClick={() => handleResetOIT()}
                            disabled={loading || respondentCounts.oit === 0}
                          >
                            <i className="fas fa-trash-alt"></i> รีเซ็ตคะแนน OIT
                          </button>
                        </div>
                      </div>

                      <div className="row mt-4">
                        <div className="col-12">
                          <button
                            className="btn btn-danger btn-lg btn-block"
                            onClick={() => handleResetAll()}
                            disabled={loading || (respondentCounts.iit === 0 && respondentCounts.eit1 === 0 && respondentCounts.eit2 === 0 && respondentCounts.oit === 0)}
                          >
                            <i className="fas fa-trash-alt"></i> รีเซ็ตคะแนนทั้งหมด
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ตารางภาพรวมทุกโรงเรียน - แสดงคะแนนแทนจำนวนผู้ตอบ */}
            {selectedFiscalYear && (
              <div className="row">
                <div className="col-12">
                  <div className="card">
                    <div className="card-header">
                      <h3 className="card-title">
                        ภาพรวมคะแนนทุกโรงเรียน
                        (ปีงบประมาณ {fiscalYears.find(y => y.id === parseInt(selectedFiscalYear))?.year})
                      </h3>
                      <div className="card-tools">
                        <button 
                          type="button" 
                          className="btn btn-tool" 
                          onClick={fetchAllSchoolsData}
                          disabled={tableLoading}
                        >
                          <i className="fas fa-sync-alt"></i> รีเฟรช
                        </button>
                      </div>
                    </div>
                    <div className="card-body table-responsive p-0">
                      {tableLoading ? (
                        <div className="text-center p-4">
                          <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">กำลังโหลด...</span>
                          </div>
                          <p className="mt-2">กำลังโหลดข้อมูล...</p>
                        </div>
                      ) : (
                        <table className="table table-hover">
                          <thead>
                            <tr>
                              <th className="text-center" style={{width: '5%'}}>#</th>
                              <th style={{width: '20%'}}>โรงเรียน</th>
                              <th className="text-center" style={{width: '8%'}}>IIT<br/>(30)</th>
                              <th className="text-center" style={{width: '8%'}}>EIT1<br/>(15)</th>
                              <th className="text-center" style={{width: '8%'}}>EIT2<br/>(15)</th>
                              <th className="text-center" style={{width: '8%'}}>OIT<br/>(40)</th>
                              <th className="text-center" style={{width: '10%'}}>คะแนนรวม<br/>(100)</th>
                              <th className="text-center" style={{width: '13%'}}>เกณฑ์</th>
                              <th className="text-center" style={{width: '20%'}}>การจัดการ</th>
                            </tr>
                          </thead>
                          <tbody>
                            {allSchoolsData.length > 0 ? (
                              allSchoolsData.map((school, index) => (
                                <tr key={school.id}>
                                  <td className="text-center">{index + 1}</td>
                                  <td>{school.name}</td>
                                  <td className={`text-center ${getScoreClass(school.iit_score)}`}>
                                    {school.iit_score > 0 ? school.iit_score : '-'}
                                  </td>
                                  <td className={`text-center ${getScoreClass(school.eit1_score)}`}>
                                    {school.eit1_score > 0 ? school.eit1_score : '-'}
                                  </td>
                                  <td className={`text-center ${getScoreClass(school.eit2_score)}`}>
                                    {school.eit2_score > 0 ? school.eit2_score : '-'}
                                  </td>
                                  <td className={`text-center ${getScoreClass(school.oit_score)}`}>
                                    {school.oit_score > 0 ? school.oit_score : '-'}
                                  </td>
                                  <td className={`text-center ${getScoreClass(school.total_score)}`}>
                                    <strong>{school.total_score > 0 ? school.total_score : '-'}</strong>
                                  </td>
                                  <td className="text-center">
                                    {school.total_score > 0 ? (
                                      <span className={`badge ${
                                        school.total_score >= 95 ? 'bg-success' : 
                                        school.total_score >= 85 ? 'bg-primary' : 
                                        school.total_score >= 75 ? 'bg-info' : 
                                        school.total_score >= 65 ? 'bg-warning' : 'bg-danger'
                                      }`}>
                                        {getRating(school.total_score)}
                                      </span>
                                    ) : (
                                      <span className="text-muted">-</span>
                                    )}
                                  </td>
                                  <td>
                                    <div className="btn-group btn-group-sm">
                                      {school.iit_count > 0 && (
                                        <button
                                          type="button"
                                          className="btn btn-danger"
                                          title="รีเซ็ต IIT"
                                          onClick={() => handleResetIIT(school.id)}
                                          disabled={loading}
                                        >
                                          <i className="fas fa-trash-alt"></i> IIT
                                        </button>
                                      )}
                                      
                                      {school.eit1_count > 0 && (
                                        <button
                                          type="button"
                                          className="btn btn-danger"
                                          title="รีเซ็ต EIT1"
                                          onClick={() => handleResetEIT1(school.id)}
                                          disabled={loading}
                                        >
                                          <i className="fas fa-trash-alt"></i> EIT1
                                        </button>
                                      )}
                                      
                                      {school.eit2_count > 0 && (
                                        <button
                                          type="button"
                                          className="btn btn-danger"
                                          title="รีเซ็ต EIT2"
                                          onClick={() => handleResetEIT2(school.id)}
                                          disabled={loading}
                                        >
                                          <i className="fas fa-trash-alt"></i> EIT2
                                        </button>
                                      )}
                                      
                                      {school.oit_count > 0 && (
                                        <button
                                          type="button"
                                          className="btn btn-danger"
                                          title="รีเซ็ต OIT"
                                          onClick={() => handleResetOIT(school.id)}
                                          disabled={loading}
                                        >
                                          <i className="fas fa-trash-alt"></i> OIT
                                        </button>
                                      )}
                                      
                                      {(school.iit_count > 0 || school.eit1_count > 0 || school.eit2_count > 0 || school.oit_count > 0) && (
                                        <button
                                          type="button"
                                          className="btn btn-danger"
                                          title="รีเซ็ตทั้งหมด"
                                          onClick={() => handleResetAll(school.id)}
                                          disabled={loading}
                                        >
                                          <i className="fas fa-trash-alt"></i> ทั้งหมด
                                        </button>
                                      )}
                                      
                                      {school.iit_count === 0 && school.eit1_count === 0 && school.eit2_count === 0 && school.oit_count === 0 && (
                                        <span className="text-muted">ไม่มีข้อมูล</span>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="9" className="text-center">ไม่พบข้อมูล</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default ResetScores;